/**
 * <squiryl-button> — a framework-agnostic Web Component that runs the buyer-facing
 * "Continue with Squiryl" flow against a merchant-hosted broker (see @squiryl/sdk
 * `createSquirylBroker`). It NEVER sees the partner key or K — it only talks to
 * your broker origin.
 *
 * Flow: click → POST {brokerBase}/squiryl/session → render the authorize_url as a QR +
 * a deep link → poll GET {brokerBase}/squiryl/session/{id} → on 'delivered' emit `squiryl:share`.
 *
 * Events (all bubble + cross shadow DOM):
 *   - squiryl:statechange  detail: { state }
 *   - squiryl:share        detail: { payload, kyc_verified }   // success
 *   - squiryl:declined     detail: {}
 *   - squiryl:expired      detail: {}
 *   - squiryl:error        detail: { error: string }
 *
 * Attributes: `endpoint` (required — your Squiryl mount path, e.g. /squiryl),
 * `poll-interval` (ms, default 3000),
 * `label` (button text), `session-path` (default '/squiryl/session'),
 * `locale` (buyer-facing language: en|es|ru|fr|de; auto-detected from
 * navigator.language when unset, falling back to en).
 */
import { messagesFor, resolveLocale, type Messages } from './i18n.js';
import QRCode from 'qrcode';

export type SquirylButtonState =
  | 'idle'
  | 'connecting'
  | 'awaiting'
  | 'done'
  | 'declined'
  | 'expired'
  | 'error';

type CreateSessionResponse = { session_id: string; authorize_url: string; expires_at: string };
type PollResponse = {
  status: 'pending' | 'claimed' | 'approved' | 'delivered' | 'declined' | 'expired';
  kyc_verified: boolean;
  payload?: unknown;
  expires_at?: string;
};

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export class SquirylButton extends HTMLElement {
  static observedAttributes = ['label', 'locale'];

  /** Overridable for tests / custom transports. Defaults to global fetch. */
  fetchImpl: FetchLike = (input, init) => fetch(input, init);

  private root: ShadowRoot;
  private state: SquirylButtonState = 'idle';
  private timer: ReturnType<typeof setTimeout> | null = null;
  private sessionId: string | null = null;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.renderIdle();
  }

  disconnectedCallback(): void {
    this.stopPolling();
  }

  attributeChangedCallback(): void {
    if (this.state === 'idle') this.renderIdle();
  }

  get currentState(): SquirylButtonState {
    return this.state;
  }

  private get brokerBase(): string {
    // `endpoint` is your Squiryl mount path (e.g. /squiryl or https://you.com/squiryl —
    // where you mounted squirylHandler()). `broker-base` is the deprecated alias.
    const base = this.getAttribute('endpoint') ?? this.getAttribute('broker-base');
    if (base == null) {
      throw new Error('<squiryl-button> requires an "endpoint" attribute — your Squiryl mount path, e.g. endpoint="/squiryl"');
    }
    return base.replace(/\/+$/, '');
  }

  private get sessionPath(): string {
    // endpoint mode appends /session (matching squirylHandler's routes). The legacy
    // broker-base mode still honours an explicit session-path.
    if (this.getAttribute('endpoint') != null) {
      return '/session';
    }
    return this.getAttribute('session-path') || '/squiryl/session';
  }

  private get pollInterval(): number {
    const raw = Number(this.getAttribute('poll-interval'));
    return Number.isFinite(raw) && raw > 0 ? raw : 3000;
  }

  /** Buyer-facing strings for the resolved locale (per render, so it tracks attribute + navigator changes). */
  private get messages(): Messages {
    return messagesFor(resolveLocale(this.getAttribute('locale')));
  }

  private setState(state: SquirylButtonState): void {
    this.state = state;
    this.emit('squiryl:statechange', { state });
  }

  private emit(type: string, detail: Record<string, unknown>): void {
    this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
  }

  /** Public entry point (also wired to the button click). */
  async start(): Promise<void> {
    if (this.state === 'connecting' || this.state === 'awaiting') return;
    this.setState('connecting');
    this.renderStatus(this.messages.connecting);
    try {
      const res = await this.fetchImpl(`${this.brokerBase}${this.sessionPath}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: '{}',
      });
      if (!res.ok) throw new Error(`session create failed: ${res.status}`);
      const session = (await res.json()) as CreateSessionResponse;
      if (!isSafeAuthorizeUrl(session.authorize_url)) {
        throw new Error('broker returned an unexpected authorize_url scheme');
      }
      this.sessionId = session.session_id;
      await this.renderAwaiting(session.authorize_url);
      this.setState('awaiting');
      this.schedulePoll();
    } catch (err) {
      this.fail((err as Error).message);
    }
  }

  private schedulePoll(): void {
    this.timer = setTimeout(() => void this.poll(), this.pollInterval);
  }

  private stopPolling(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private async poll(): Promise<void> {
    if (!this.sessionId || this.state !== 'awaiting') return;
    try {
      const res = await this.fetchImpl(`${this.brokerBase}${this.sessionPath}/${encodeURIComponent(this.sessionId)}`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`poll failed: ${res.status}`);
      const poll = (await res.json()) as PollResponse;
      const m = this.messages;

      switch (poll.status) {
        case 'delivered':
          this.stopPolling();
          this.setState('done');
          this.renderStatus(`${m.shared} ✓`);
          this.emit('squiryl:share', { payload: poll.payload ?? null, kyc_verified: poll.kyc_verified });
          return;
        case 'declined':
          this.stopPolling();
          this.setState('declined');
          this.renderStatus(m.declined);
          this.emit('squiryl:declined', {});
          return;
        case 'expired':
          this.stopPolling();
          this.setState('expired');
          this.renderStatus(m.expired);
          this.emit('squiryl:expired', {});
          return;
        default:
          this.schedulePoll(); // pending / claimed / approved → keep waiting
      }
    } catch (err) {
      this.fail((err as Error).message);
    }
  }

  private fail(message: string): void {
    this.stopPolling();
    this.setState('error');
    this.renderStatus(this.messages.error);
    this.emit('squiryl:error', { error: message });
  }

  // ---- rendering (minimal, unopinionated) ------------------------------------

  private renderIdle(): void {
    const label = this.getAttribute('label') || this.messages.continueButton;
    this.root.innerHTML = `<style>${STYLES}</style>
      <button part="button" type="button" class="sq-btn">${escapeHtml(label)}</button>`;
    const btn = this.root.querySelector('button');
    btn?.addEventListener('click', () => void this.start());
  }

  private renderStatus(text: string): void {
    const el = this.root.querySelector('.sq-status');
    if (el) el.textContent = text;
    else this.root.innerHTML = `<style>${STYLES}</style><div part="status" class="sq-status">${escapeHtml(text)}</div>`;
  }

  private async renderAwaiting(authorizeUrl: string): Promise<void> {
    // `authorizeUrl` scheme is validated in start() before we get here, so it is
    // safe to place in an href. The QR SVG is generated by the qrcode library from
    // that same validated URL — a trusted, self-produced fragment (no user HTML).
    const m = this.messages;
    let svg = '';
    try {
      svg = await QRCode.toString(authorizeUrl, { type: 'svg', margin: 1 });
    } catch {
      svg = ''; // QR is best-effort; the deep link below always works
    }

    this.root.replaceChildren();
    const style = document.createElement('style');
    style.textContent = STYLES;

    const panel = document.createElement('div');
    panel.setAttribute('part', 'panel');
    panel.className = 'sq-panel';

    const qr = document.createElement('div');
    qr.setAttribute('part', 'qr');
    qr.className = 'sq-qr';
    qr.innerHTML = svg; // trusted qrcode-generated SVG over a scheme-validated URL

    const hint = document.createElement('p');
    hint.className = 'sq-hint';
    hint.textContent = m.scanHint;

    const link = document.createElement('a');
    link.setAttribute('part', 'link');
    link.className = 'sq-link';
    link.setAttribute('href', authorizeUrl);
    link.textContent = m.openInApp;

    const status = document.createElement('div');
    status.setAttribute('part', 'status');
    status.className = 'sq-status';
    status.textContent = m.waiting;

    panel.append(qr, hint, link, status);
    this.root.append(style, panel);
  }
}

/** Allowlist the deep-link/universal-link schemes we expect from the broker. */
function isSafeAuthorizeUrl(url: string): boolean {
  return /^(squiryl:|https:)/i.test(url.trim());
}

/**
 * Themeable through CSS custom properties.
 *
 * The button lives in a shadow root, so a host page cannot reach it with an
 * ordinary selector: every integration used to get the same #3399ff button in
 * system-ui, which looks wrong on any site with a design of its own and is
 * unreadable on a dark one. Custom properties DO inherit through a shadow
 * boundary, so a page themes the widget by declaring these on any ancestor.
 *
 * Every property falls back to the previous hardcoded value, so an integration
 * that sets nothing renders exactly as it did before.
 */
const STYLES = `
:host {
  display: inline-block;
  font-family: var(--squiryl-font, system-ui, sans-serif);
}
.sq-btn {
  cursor: pointer; border: 0; font-weight: 600;
  padding: var(--squiryl-button-padding, 10px 16px);
  border-radius: var(--squiryl-button-radius, 8px);
  background: var(--squiryl-button-bg, #3399ff);
  color: var(--squiryl-button-fg, #fff);
  font-size: var(--squiryl-button-size, 15px);
  letter-spacing: var(--squiryl-button-tracking, normal);
  text-transform: var(--squiryl-button-transform, none);
}
.sq-btn:hover { background: var(--squiryl-button-bg-hover, var(--squiryl-button-bg, #3399ff)); }
.sq-btn:focus-visible { outline: 2px solid var(--squiryl-focus, currentColor); outline-offset: 2px; }
.sq-panel {
  display: inline-flex; flex-direction: column; align-items: center; gap: 8px; padding: 16px;
  border: 1px solid var(--squiryl-panel-border, #e2e8f0);
  border-radius: var(--squiryl-panel-radius, 12px);
  background: var(--squiryl-panel-bg, transparent);
}
.sq-qr { background: var(--squiryl-qr-bg, transparent); padding: var(--squiryl-qr-pad, 0); border-radius: var(--squiryl-panel-radius, 12px); }
.sq-qr svg { width: var(--squiryl-qr-size, 180px); height: var(--squiryl-qr-size, 180px); display: block; }
.sq-hint { margin: 0; font-size: 13px; color: var(--squiryl-muted, #475569); }
.sq-link { color: var(--squiryl-link, #3399ff); font-weight: 600; text-decoration: none; }
.sq-status { font-size: 13px; color: var(--squiryl-muted, #475569); }
`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

if (typeof customElements !== 'undefined' && !customElements.get('squiryl-button')) {
  customElements.define('squiryl-button', SquirylButton);
}
